<template>
  <div class="checklist-inspection-right-pane-table">
    <v-row class="mb-4 mt-2 top-section-class">
      <v-col cols="12" class="search-checklist">
        <SearchBar
          @onSearch="onSearch"
          type="button"
          :label="activeChips.length > 0 ? 'More Filters' : 'Filters'"
          :length="activeChips.length"
          :chipText="activeChips"
          ghostText="Search Criteria"
          @onClick="onSearchBarClick"
          @removeFilter="onRemoveFilter"
          :valueSearch="filterText"
          :counterSection="true"
          :levelCounter="levelCounter"
          @filterTypes="onFilterTypes"
          :removeCounterLevelFilter="removeCounterLevelFilter"
          style="width: 100%;"
        />
        <div class="grand-badge-dropdown">
          <GrandBadgeDropdown
            :value="currentBadge"
            :level="readyToGrantLevel"
            :badge-id="currentBadgeId"
            :has-active-checkpoints="hasActiveCheckpoints"
            @input="onIssueBadge"
            @remove="onIssueBadge"
            :isWhiteChevron="true"
          />
        </div>
      </v-col>
    </v-row>
    <NoRecordFound
      v-if="filteredItems.length === 0"
      :should-show-add-action="false"
      :title="'Checkpoints'"
      :message="'Checkpoints'"
      :subtitle="''"
      :description="noRecordDescription"
      class="no-record-found"
      :noBorder="true"
      :showActiveCounter="hasActiveFiltersOrSearch"
      :showSpecificMessage="noRecordMessage"
      :showClearAction="hasActiveFiltersOrSearch"
      :clearLabel="clearBtnLabel"
      @onClear="onClearSelection(null)"
    />
    <DxDataGrid
      v-else
      :key="dataGridRenderKey"
      :data-source="pagedItems"
      :column-auto-width="true"
      :word-wrap-enabled="true"
      :show-row-lines="true"
      :show-column-lines="false"
      :show-borders="false"
      :element-attr="{ class: 'checklist-inspection-table-grid' }"
      :allow-column-reordering="false"
      :allow-column-resizing="false"
      :allow-sorting="false"
      no-data-text=""
      :loadPanel="{ enabled: false }"
    >
      <DxColumn
        data-field="level"
        caption="Level"
        cell-template="levelCell"
        :width="72"
        :allow-sorting="false"
      />
      <DxColumn
        data-field="checkpoint"
        caption="Checkpoint"
        cell-template="checkpointCell"
        :allow-sorting="false"
      />
      <DxColumn
        data-field="status"
        caption="Status"
        cell-template="statusCell"
        :width="160"
        :allow-sorting="false"
      />
      <DxColumn
        caption=""
        cell-template="actionCell"
        :width="120"
        :allow-sorting="false"
      />

      <template #levelCell="{ data }">
        <div class="level-cell">
          <MinimalisticReadinessLevel :level="toLowerLevel(data.data.level) || 'apprentice'" />
        </div>
      </template>

      <template #checkpointCell="{ data }">
        <span class="checkpoint-text"><span class="checkpoint-prefix">{{ data.data.prefix }}</span> {{ data.data.checkpoint }}</span>
      </template>

      <template #statusCell="{ data }">
      <div>
        <CheckpointInspectionStatusBadge
          :checkpoint_status="data.data.status === 'Not Applicable' ? 'N/A' : data.data.status"
          :inspected_by="data.data.inspected_by"
          :inspected_by_avatar="data.data.inspected_by_avatar"
          :inspected_on="data.data.inspected_on"
          :reason="data.data.reason"
          :onClose="() => emit('onClose', data.data)"
          @onChatterClick="emit('onChatterClick', { ...data.data, ...$event })"
        />
      </div>
      </template>

      <template #actionCell="{ data }">
        <div class="action-cell">
          <StandardButton
            type="magenta"
            size="small"
            label="Inspect"
            @click="$emit('onInspect', data.data)"
          />
        </div>
      </template>
    </DxDataGrid>
    <ExPagination
      v-if="filteredItems.length > PER_PAGE"
      :pagination="pagination"
      @onRequest="onPageRequest"
    />
    <SearchModal
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="onCheckedItemsChanged"
      :clearSelection="clearSelection"
      filterType="Evaluation Checkpoints"
      :filterGroups="filterGroups"
      :statusOptions="[]"
      :defaultSelected="activeChips"
      :modalWidth="480"
    />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, watch } from "vue";
import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import MinimalisticReadinessLevel from "@components/MinimalisticReadinessLevel/MinimalisticReadinessLevel.vue";
import CheckpointInspectionStatusBadge from "@components/CheckpointInspectionStatusBadge/CheckpointInspectionStatusBadge.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import GrandBadgeDropdown from "@components/EvaluationGrandBadge/GrandBadgeDropdown.vue";

import { getBadgeLevelDisplayLabel, isLevelSelected, normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  currentBadgeLevel: {
    type: String,
    default: null,
  },
  currentBadgeId: {
    type: [String, Number],
    default: null,
  },
  resetFilterToNotYetPassedCount: {
    type: Number,
    default: 0,
  },
  /** Rule 13: with active checkpoints the dropdown must not offer "Give". */
  hasActiveCheckpoints: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onInspect", "issueBadge", "filterChanged", "filteredCheckpointIdsChanged", "onClose", "onChatterClick"]);

const filterGroups = [
  {
    label: "Status",
    items: [
      { label: "No Filter", value: "No Filter" },
      { label: "Not Yet Passed", value: "Not Yet Passed" },
      { label: "Only Failed", value: "Only Failed" },
      { label: "Only Not Yet Evaluated", value: "Only Not Yet Evaluated" },
      { label: "Only Reset", value: "Only Reset" },
      { label: "Only Not Applicable", value: "Only Not Applicable" },
      { label: "Only Passed", value: "Only Passed" },
    ],
  },
];

const PER_PAGE = 10;

/** Force grid remount when checkpoint rows change so status cells stay in sync with API reloads. */
const dataGridRenderKey = ref(0);

const filterText = ref("");
const activeChips = ref(["Not Yet Passed"]);
const selectedLevels = ref([]);
const removeCounterLevelFilter = ref(false);
const currentPage = ref(1);
const showFilterDialog = ref(false);
const clearSelection = ref("");

const currentBadge = computed(() => props.currentBadgeLevel ?? null);

const normalizeLevelValue = (level) => {
  if (typeof level === "string") {
    return level.trim();
  }

  if (typeof level === "number") {
    return String(level);
  }

  return "";
};

const toLowerLevel = (level) => normalizeLevelValue(level).toLowerCase();

const toDisplayLevel = (level) => {
  const normalizedLevel = toLowerLevel(level);

  if (!normalizedLevel) {
    return null;
  }

  return (
    normalizedLevel.charAt(0).toUpperCase() + normalizedLevel.slice(1)
  );
};

const readyToGrantLevel = computed(() => {
  const levels = ['Apprentice', 'Proficient', 'Coach', 'Architect'];
  let highestReady = null;
  for (const level of levels) {
    const levelItems = props.items.filter(
      (item) => normalizeBadgeLevel(item.level) === normalizeBadgeLevel(level)
    );
    if (levelItems.length > 0 && levelItems.every((item) => item.status === 'Passed')) {
      highestReady = level;
    }
  }
  return highestReady;
});

const matchesStatusChip = (item, chip) => {
  const status = item.status ?? "";
  switch (chip) {
    case "Not Yet Passed":
      return status !== "Passed" && status !== "Not Applicable" && status !== "N/A";
    case "Only Failed":
      return status === "Failed";
    case "Only Not Yet Evaluated":
      return status === "Pending";
    case "Only Reset":
      return status === "Reset";
    case "Only Not Applicable":
      return status === "N/A" || status === "Not Applicable";
    case "Only Passed":
      return status === "Passed";
    default:
      return true;
  }
};

const itemsRefreshSignature = computed(() =>
  (props.items || [])
    .map((row) =>
      [
        row.checkpoint_id ?? row.id,
        row.status ?? "",
        row.reason ?? "",
        row.inspected_on ?? "",
      ].join(":")
    )
    .join("|")
);

watch(itemsRefreshSignature, () => {
  dataGridRenderKey.value += 1;
});

const filteredItems = computed(() => {
  let result = props.items;

  if (selectedLevels.value.length > 0) {
    result = result.filter((item) =>
      isLevelSelected(selectedLevels.value, item.level)
    );
  }

  if (filterText.value.trim()) {
    const query = filterText.value.trim().toLowerCase();
    result = result.filter((item) =>
      item.checkpoint?.toLowerCase().includes(query)
    );
  }

  const effectiveChips = activeChips.value.filter(
    (c) => c && c !== "No Filter"
  );
  if (effectiveChips.length > 0) {
    result = result.filter((item) =>
      effectiveChips.every((chip) => matchesStatusChip(item, chip))
    );
  }

  return result;
});

const levelCounter = computed(() => {
  const counter = { Apprentice: 0, Proficient: 0, Coach: 0, Architect: 0 };
  const effectiveChips = activeChips.value.filter(
    (c) => c && c !== "No Filter"
  );

  let itemsToCount = props.items;
  if (effectiveChips.length > 0) {
    itemsToCount = itemsToCount.filter((item) =>
      effectiveChips.every((chip) => matchesStatusChip(item, chip))
    );
  }
  itemsToCount.forEach((item) => {
    const level = getBadgeLevelDisplayLabel(item.level) || toDisplayLevel(item.level);
    if (level && counter[level] !== undefined) {
      counter[level]++;
    }
  });
  return counter;
});

watch(filteredItems, (items) => {
  currentPage.value = 1;
  emit(
    "filteredCheckpointIdsChanged",
    items.map((item) => item.checkpoint_id).filter(Boolean)
  );
}, { immediate: true });

watch(
  () => props.resetFilterToNotYetPassedCount,
  (count) => {
    if (count > 0) {
      activeChips.value = ["Not Yet Passed"];
      currentPage.value = 1;
    }
  }
);

const hasSearchActive = computed(() => !!filterText.value.trim());
const hasFiltersActive = computed(
  () => activeChips.value.length > 0 || selectedLevels.value.length > 0
);

const hasActiveFiltersOrSearch = computed(
  () => hasSearchActive.value || hasFiltersActive.value
);

watch(hasFiltersActive, (val) => {
  emit("filterChanged", val);
}, { immediate: true });

const clearBtnLabel = computed(() => {
  if (hasSearchActive.value && hasFiltersActive.value) return "Clear search and filters";
  if (hasSearchActive.value) return "Clear search";
  if (hasFiltersActive.value) return "Clear filters";
  return "Clear search and filters";
});

const noRecordMessage = computed(() => {
  if (!hasActiveFiltersOrSearch.value) return "No checkpoints exist";
  if (hasSearchActive.value && hasFiltersActive.value)
    return "Your search and/or filter did not match any checkpoints. Please try again";
  if (hasSearchActive.value)
    return "Your search did not match any checkpoints. Please try again";
  return "Your filter did not match any checkpoints. Please try again";
});

const noRecordDescription = computed(() =>
  hasActiveFiltersOrSearch.value ? "" : "No checkpoints exist for this responsibility"
);

const pagination = computed(() => ({
  total: filteredItems.value.length,
  page: currentPage.value,
  perPage: PER_PAGE,
}));

const pagedItems = computed(() => {
  const start = (currentPage.value - 1) * PER_PAGE;
  return filteredItems.value.slice(start, start + PER_PAGE);
});

const onPageRequest = (event) => {
  currentPage.value = Number(event?.page ?? 1);
};

const onSearch = (value) => {
  filterText.value = value ?? "";
};

const onFilterTypes = (levels) => {
  selectedLevels.value = Array.isArray(levels)
    ? levels.map((l) => l.toLowerCase())
    : [];
};

const onCheckedItemsChanged = (event) => {
  const mapped = Array.isArray(event)
    ? event
        .map((item) => {
          if (item == null) return null;
          if (typeof item === "string") return item;
          if (typeof item === "object") {
            // SearchModal emits objects like { label: '...', value: '...' }
            // or { labelType: '...', value: '...' }
            return item.value || item.label;
          }
          return null;
        })
        .filter(Boolean)
    : [];

  // If "No Filter" is selected, clear all status filters
  if (mapped.includes("No Filter")) {
    activeChips.value = [];
  } else {
    activeChips.value = mapped;
  }
  
  showFilterDialog.value = false;
};

const onRemoveFilter = (chip) => {
  const removed = String(chip ?? "").trim();
  if (removed) {
    activeChips.value = activeChips.value.filter((c) => c !== removed);
    clearSelection.value = removed;
    nextTick(() => {
      clearSelection.value = "";
    });
  }
};

const onClearSelection = (event) => {
  if (event) {
    const removed = String(event ?? "").trim();
    if (removed) {
      activeChips.value = activeChips.value.filter((c) => c !== removed);
    }
  } else {
    activeChips.value = [];
    filterText.value = "";
    selectedLevels.value = [];
    removeCounterLevelFilter.value = true;
    nextTick(() => {
      removeCounterLevelFilter.value = false;
    });
  }
};

const onSearchBarClick = (type) => {
  if (type === "filter") {
    showFilterDialog.value = true;
  }
};

const onIssueBadge = (event) => {
  if (event?.isRemoveAction) {
    emit("issueBadge", { ...event });
    return;
  }
  const data = { ...event, level: event?.value ?? event?.level };
  emit("issueBadge", data);
};

</script>

<style scoped>
.checklist-inspection-right-pane-table {
  width: 100%;
  background: #fff;
}

.top-section-class {
  padding: 0px 15px;
}

.level-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkpoint-text {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  line-height: 20px;
}

.checkpoint-prefix {
  text-transform: capitalize;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

::v-deep .checklist-inspection-table-grid {
  width: 100%;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > td,
::v-deep .dx-datagrid-content .dx-datagrid-table .dx-row > tr > td {
  vertical-align: middle;
  padding: 12px 16px;
  height: auto !important;
}

::v-deep .dx-datagrid-content .dx-datagrid-table .dx-header-row > td,
::v-deep .dx-datagrid-content .dx-datagrid-table .dx-header-row > tr > td {
  padding: 10px 16px !important;
}

::v-deep .dx-datagrid-headers {
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  background-color: #f9fafb;
  border-top: 1px solid #eaecf0;
}

::v-deep .dx-datagrid-headers .dx-datagrid-table .dx-row > td,
::v-deep .dx-datagrid .dx-row-lines > td {
  border-bottom: 1px solid #eaecf0 !important;
}

::v-deep .dx-datagrid-rowsview {
  border-top: none;
}

::v-deep .dx-datagrid {
  background: #fff;
}

::v-deep .input-search {
    margin-top: 0 !important;
}
.search-checklist {
  display: flex;
  gap: 16px;
}
.grand-badge-dropdown {
  margin: 8px 0px;
}
::v-deep .ex-grand-badge-picker  {
  width: 147px;
  height: 44px;
  background: #7F56D9;
  border-radius: 8px;
  border: 1px solid #7F56D9;
  background: #7F56D9;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

::v-deep .ex-grand-badge-picker .select-item-level {
  color: white;
}

::v-deep .ex-grand-badge-picker .level-picker fieldset {
  border: none !important;
}

::v-deep .ex-grand-badge-picker .level-picker.v-input--is-focused fieldset {
  border: none !important;
  box-shadow: none !important;
}

::v-deep .ex-grand-badge-picker .v-input__slot fieldset {
  border: none !important;
  box-shadow: none !important;
}

::v-deep .select-item-level {
  color: #FFF;
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
}

::v-deep .ex-grand-badge-picker .select-item-level {
  margin-top: 6px !important;
}

::v-deep .ex-grand-badge-picker .v-input__icon.v-input__icon--append {
  margin-top: 4px !important;
}

::v-deep .ex-grand-badge-picker .level-picker .v-input__icon svg {
  width: 20px;
  height: 20px;
}

::v-deep .ex-grand-badge-picker .select-level-list {
  right: -40px !important;
}

::v-deep .search-button .ex_text_input  {
  min-width: 100% !important;
}
</style>
